import * as Yup from 'yup';
import { useFieldArray } from 'react-hook-form';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { useSaveData } from '../../../hooks/useSaveData';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { CreateTimeAndMaterialButton } from './create-time-and-material.button';
import { DeleteButton } from '../../../components/delete-button.component';
import { Icons } from '../../../assets/icons';
import { CreateBuildingFloorUnitButton } from './create-building-floor-unit.button';
import { useEffect, useRef, useState } from 'react';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { proctorResultApiKeyStore } from '../../../api/proctor-result-api-key-store';
import { FormAutoCompleteSelectDynamic } from '../../../components/hook-form/form-autocomplete-select.dynamic.component';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { GenericFileUploadDialog } from '../../../components/file-upload-dialog.component';
import SampleFileForDownload from './sample-file-for-download';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useAuthorize } from '../../../hooks/use-authorize';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { parseErrorMessageRecursive } from '../../../utils/yup-error-parser.util';
import { FormSelect } from '../../query-builder/hooks-form-querybuilder/form-select.component';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { FormSelectDynamic } from '../../../components/hook-form/form-select.dynamic.component';
import { getAddressAbbreviationError } from '../../../utils/abbreviations.util';

interface BuildingUnitsListFormProps {
  projectID: number;
  singleOrMultiLots: any[];
  projectTypeValue: any;
  isTimeAndMaterial: boolean;
  page?: string;
  refetch: () => void;
  syncWithDux: any;
  requestID?: any;
  isRequestType?: boolean;
}

export interface SingleOrMultiLot {
  unitID: number;
  buildingID: number | null;
  unitName: string;
  unitNumber: string | null;
  floorID: number | null;
  isCommonArea: boolean | null;
  commonAreaID: number | null;
  address: string;
  city: string | null;
  stateID: number | null;
  zipcode: string | null;
  countyID: number | null;
  municipalityID: number | null;
  jobNumber: string;
  jobsite: string | null;
  proctorResult: string | null;
  disableDelete: boolean | null;
  isUnitCreationCompleted: boolean | null;
  squareFeet: number;
  lotTypeChanged?: boolean;
  isApplyAppropriatePLConfirmed?: boolean;
}

export interface LotData {
  projectID: number | null;
  projectBuildings: any;
  singleOrMultiLots: SingleOrMultiLot[];
}

const proctorResultSchema = Yup.object().shape({
  proctorResultsID: Yup.number().required('Proctor result id is required'),
  unitID: Yup.number().required('Unit id is required'),
  proctorFileName: Yup.string().required('Proctor file name is required'),
});

const lotValidationSchema = Yup.object().shape({
  unitName: Yup.string().required('Lot/Unit is required').max(9, 'Lot/Unit cannot exceed 9 characters'),
  address: Yup.string().required('Address is required').max(31, 'Address cannot exceed 31 characters'),
  jobsite: Yup.string().required('Jobsite is required'),
  lotType: Yup.string()
    .nullable()
    .when('isUnitCreationCompleted', ([isUnitCreationCompleted], schema) => {
      return isUnitCreationCompleted !== true
        ? schema.required('Lot Type is required')
        : schema.notRequired();
    }),
});

const lotTypeOption = [
  { value: 'TH', label: 'Townhome (TH)' },
  { value: 'SFR', label: 'Single-Family Residential (SFR)' },
];

const lotTypeCMOption = [
  { value: 'CM', label: 'Commercial (CM)' },
];

const timeAndMaterialValidationSchema = Yup.object().shape({
  singleOrMultiLots: Yup.array().of(lotValidationSchema),
});

export const TimeAndMaterialEditForm = ({
  projectID,
  isTimeAndMaterial,
  singleOrMultiLots,
  page,
  refetch,
  syncWithDux,
  projectTypeValue,
  requestID,
  isRequestType,
}: BuildingUnitsListFormProps) => {
  const form = useReactHookForm({
    defaultValues: {
      projectID,
      singleOrMultiLots,
      lotType: '',
    },
    yupSchema: timeAndMaterialValidationSchema,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'singleOrMultiLots',
    keyName: 'tID',
  });

  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { isLoading, mutateAsync } = useSaveData(projectsApiKeyStore.updateLotInfo());

  // ── Refs ──────────────────────────────────────────────────────────────────
  const initialJobSites = useRef<string[]>(fields?.map((field: any) => field.jobsite || ''));
  const previousLotTypes = useRef<Record<number, string | null>>({}); // null = loaded with no lot type
  // ✅ Tracks the original server-saved lot type — never mutated after initialisation
  const originalLotTypes = useRef<Record<number, string | null>>({});
  const lotTypeChangedFlags = useRef<Record<number, boolean>>({});

  const { data } = useLoadData<ApiResponse<any>>(proctorResultApiKeyStore.getProjectProctorResults(projectID));

  // ── State ─────────────────────────────────────────────────────────────────
  const [isBulkLotDialogOpen, setIsBulkLotDialogOpen] = useState<boolean>(false);
  const [isBulkAddressDialogOpen, setIsBulkAddressDialogOpen] = useState<boolean>(false);
  const [transformedValues, setTransformedValues] = useState<any>({});
  const [openDialog, setOpenDialog] = useState(false);
  const [lotTypeChangeDialog, setLotTypeChangeDialog] = useState<{
    open: boolean;
    index: number;
    newValue: string;
    previousValue: string;
  }>({ open: false, index: -1, newValue: '', previousValue: '' });
  const [lotTypePricingFlags, setLotTypePricingFlags] = useState<Record<number, boolean>>({});

  const { isLoading: isLoadingFile, mutateAsync: UploadFileForBulkLotOrAddressCreation } = useSaveData<LotData>(
    projectsApiKeyStore.uploadFileForBulkLotOrAddressCreation()
  );

  const { isLoading: isLoadingFileForAddressUpload, mutateAsync: UploadFileForBulkAddressCreation } =
    useSaveData<LotData>(projectsApiKeyStore.uploadFileForBulkAddressCreation());

  // ── Initialise previousLotTypes + originalLotTypes refs when fields load ───
  // Store null explicitly so the handler can distinguish:
  //   undefined → row never initialised
  //   null      → row loaded from server with no lot type
  //   string    → row has a real saved lot type
  useEffect(() => {
    fields?.forEach((field: any, index: number) => {
      if (previousLotTypes.current[index] === undefined) {
        const currentValue = form.getValues(`singleOrMultiLots.${index}.lotType`);
        previousLotTypes.current[index] = currentValue ?? null;
        // ✅ originalLotTypes is seeded once and never overwritten
        originalLotTypes.current[index] = currentValue ?? null;
      }
    });
  }, [fields]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const normalize = (text: string) => text?.toLowerCase()?.trim()?.replace(/\s+/g, '');

  const handleLoadCreated = (lot: any) => {
    append(lot);
    initialJobSites.current.push(lot.jobsite || '');
  };

  const handleRemove = (index: number) => {
    remove(index);
    initialJobSites.current.splice(index, 1);
  };

  // ── Data transformation before submit ─────────────────────────────────────
  const transformData = (data: any) => {
    return {
      ...data,
      singleOrMultiLots: data.singleOrMultiLots.map((unit: any, index: number) => ({
        ...unit,
        reqStatusID: unit?.reqStatusID ?? null,
        squareFeet: !unit?.squareFeet ? null : Number(unit?.squareFeet),
        lotTypeChanged: lotTypeChangedFlags.current[index] ?? false,
        isApplyAppropriatePLConfirmed: unit?.isApplyAppropriatePLConfirmed ?? false,
        proctorResult: !unit.proctorResult
          ? null
          : {
              proctorResultsID: unit.proctorResult.proctorResultsID,
              unitID: unit.unitID,
              proctorFileName: unit.proctorResult.proctorFileName,
            },
      })),
    };
  };

  const handleSubmit = async (values: any) => {
    const transformedData = transformData(values);

    const { type, ...dataWithoutType } = transformedData;
    const hasCreateWOTrue = transformedData?.singleOrMultiLots?.some((item: any) => item?.createWO === true);

    if (transformedData?.singleOrMultiLots?.length > 0 && !hasCreateWOTrue && type == 'with-workorder') {
      showSnackbar(`Please select at least one 'Create Work Order' before saving.`, 'error');
      return;
    }

    if (hasCreateWOTrue && type == 'without-workorder') {
      showSnackbar(`Please do not select 'Create Work Order' before saving.`, 'error');
      return;
    }

    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: type == 'with-workorder' } }).then(
      (data: any) => {
        const lots = data?.data?.singleOrMultiLots;
        initialJobSites.current = lots?.map((field: any) => field.jobsite || '');
        form.setValue('singleOrMultiLots', lots);
        setLotTypePricingFlags({});
        // Reset both refs after successful save
        lotTypeChangedFlags.current = {};
        lots?.forEach((lot: any, index: number) => {
          previousLotTypes.current[index] = lot.lotType ?? null;
          // ✅ After save, the new lot type becomes the new "original" for future edits
          originalLotTypes.current[index] = lot.lotType ?? null;
        });
        refetch();
      }
    );
  };

  // ── Duplicate / abbreviation validators ──────────────────────────────────
  const handleUnitNameChange = (index: number, value: string) => {
    const initialJobSite = initialJobSites?.current[index];
    const newJobSite = `${initialJobSite}${value}`?.trim();
    form.setValue(`singleOrMultiLots.${index}.jobsite`, newJobSite);

    if (!value || value?.trim() === '') {
      form.clearErrors(`singleOrMultiLots.${index}.unitName`);
      return;
    }

    const allUnits = form.getValues('singleOrMultiLots')?.map((u: any) => normalize(u?.unitName));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.unitName`, {
        type: 'manual',
        message: 'Lot/Unit name already exists.',
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.unitName`);
    }
  };

  const handleJobNumberChange = (index: number, value: string) => {
    if (!value || value?.trim() === '') {
      form.clearErrors(`singleOrMultiLots.${index}.jobNumber`);
      return;
    }

    const allUnits = form.getValues('singleOrMultiLots')?.map((u: any) => normalize(u?.jobNumber));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.jobNumber`, {
        type: 'manual',
        message: 'Job number already exists.',
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.jobNumber`);
    }
  };

  const handleAddressChange = (index: number, value: string) => {
    if (!value || value?.trim() === '') {
      form.clearErrors(`singleOrMultiLots.${index}.address`);
      return;
    }

    const abbrError = getAddressAbbreviationError(value);
    const allUnits = form.getValues('singleOrMultiLots')?.map((u: any) => normalize(u?.address));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (abbrError) {
      form.setError(`singleOrMultiLots.${index}.address`, {
        type: 'manual',
        message: abbrError,
      });
    } else if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.address`, {
        type: 'manual',
        message: 'Address already exists.',
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.address`);
    }
  };

  const handleJobsiteNameChange = (index: number, value: any) => {
    initialJobSites.current[index] = value;

    if (!value || value?.trim() === '') {
      form.clearErrors(`singleOrMultiLots.${index}.jobsite`);
      return;
    }

    const allUnits = form.getValues('singleOrMultiLots')?.map((u: any) => normalize(u?.jobsite));
    const isDuplicate = allUnits?.filter((u: string) => u === normalize(value))?.length > 1;

    if (isDuplicate) {
      form.setError(`singleOrMultiLots.${index}.jobsite`, {
        type: 'manual',
        message: 'Jobsite already exists.',
      });
    } else {
      form.clearErrors(`singleOrMultiLots.${index}.jobsite`);
    }
  };

  // ── Lot type change handler ───────────────────────────────────────────────
  /**
   * previousLotTypes.current[index] has three possible states:
   *   undefined → row not yet initialised (should not happen after useEffect)
   *   null      → row was loaded from server with no lot type
   *   string    → row has/had a real saved lot type
   *
   * Dialog rules:
   *   - same value re-selected                         → no-op, NO dialog
   *   - previous is null/empty                         → first-time pick, NO dialog, silent apply
   *   - isUnitCreationCompleted = false/null           → unit not yet saved, NO dialog, silent apply
   *   - previous is real string AND
   *     isUnitCreationCompleted = true AND value differ → SHOW pricing confirmation dialog
   */
  const handleLotTypeChange = (index: number, newValue: string) => {
    const previousValue = previousLotTypes.current[index]; // null | string | undefined
    // ✅ Compare against the original server-saved value, not the last-picked value
    const originalValue = originalLotTypes.current[index];  // null | string

    const isSameAsPrevious = previousValue === newValue;
    // ✅ If the user picks back the original saved value → treat as reverted, no dialog
    const isRevertedToOriginal = !!originalValue && originalValue === newValue;

    // ✅ Only show the pricing confirmation dialog when the unit is already created
    const isUnitCreationCompleted = form.getValues(`singleOrMultiLots.${index}.isUnitCreationCompleted`);

    if (isSameAsPrevious) {
      // Exact same value as the last pick → silent no-op
      return;
    }

    if (!isUnitCreationCompleted || !originalValue || isRevertedToOriginal) {
      // Unit not yet created, OR original was null (first-time pick),
      // OR user reverted back to the original saved value → silent apply, no dialog
      lotTypeChangedFlags.current[index] = false;
      previousLotTypes.current[index] = newValue;
      form.setValue(`singleOrMultiLots.${index}.lotType`, newValue);
      // ✅ When reverting to original, reset both flags so submit sends false/false
      if (isRevertedToOriginal) {
        form.setValue(`singleOrMultiLots.${index}.lotTypeChanged`, false);
        form.setValue(`singleOrMultiLots.${index}.isApplyAppropriatePLConfirmed`, false);
      }
    } else {
      // Real change on an already-created unit away from its original value → show dialog
      lotTypeChangedFlags.current[index] = true;
      setLotTypeChangeDialog({ open: true, index, newValue, previousValue: previousValue as string });
    }
  };

  /**
   * User clicked "Yes" — apply new lot type + mark pricing confirmed
   * lotTypeChanged: true | isApplyAppropriatePLConfirmed: true
   */
  const handleLotTypeConfirmYes = () => {
    const { index, newValue } = lotTypeChangeDialog;
    form.setValue(`singleOrMultiLots.${index}.lotType`, newValue);
    form.setValue(`singleOrMultiLots.${index}.isApplyAppropriatePLConfirmed`, true);
    previousLotTypes.current[index] = newValue;
    setLotTypePricingFlags((prev) => ({ ...prev, [index]: true }));
    setLotTypeChangeDialog({ open: false, index: -1, newValue: '', previousValue: '' });
  };

  /**
   * User clicked "No" — apply new lot type but skip pricing update
   * lotTypeChanged: true | isApplyAppropriatePLConfirmed: false
   */
  const handleLotTypeConfirmNo = () => {
    const { index, newValue } = lotTypeChangeDialog;
    form.setValue(`singleOrMultiLots.${index}.lotType`, newValue);
    form.setValue(`singleOrMultiLots.${index}.isApplyAppropriatePLConfirmed`, false);
    previousLotTypes.current[index] = newValue;
    setLotTypePricingFlags((prev) => ({ ...prev, [index]: false }));
    setLotTypeChangeDialog({ open: false, index: -1, newValue: '', previousValue: '' });
  };

  // ── Bulk upload handlers ──────────────────────────────────────────────────
  const handleSubmitBulkLotCreationFileSave = async (file: File | null) => {
    if (!file || !projectID) return;
    try {
      const formData = new FormData();
      formData.append('ProjectID', projectID.toString());
      formData.append('DataFile', file);
      const response = await UploadFileForBulkLotOrAddressCreation({ data: formData });
      const lots = response?.data?.singleOrMultiLots;
      if (Array.isArray(lots)) {
        lots.forEach((lot) => {
          append(lot);
          initialJobSites.current.push(lot.jobsite || '');
        });
      }
    } catch (error) {
      console.error('Bulk lot upload failed:', error);
    } finally {
      setIsBulkLotDialogOpen(false);
    }
  };

  const handleSubmitBulkAddressCreationFileSave = async (file: File | null) => {
    if (!file || !projectID) return;
    try {
      const formData = new FormData();
      formData.append('ProjectID', projectID.toString());
      formData.append('DataFile', file);
      const response = await UploadFileForBulkAddressCreation({ data: formData });
      const lots = response?.data?.singleOrMultiLots;
      form.setValue('singleOrMultiLots', lots);
      initialJobSites.current = lots?.map((field: any) => field.jobsite || '');
    } catch (error) {
      console.log('Bulk Address upload failed:', error);
    } finally {
      setIsBulkAddressDialogOpen(false);
    }
  };

  // ── Work-order dialog handlers ────────────────────────────────────────────
  const closeDialog = async () => {
    const { type, ...dataWithoutType } = transformedValues;
    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: false } }).then((data: any) => {
      const lots = data?.data?.singleOrMultiLots;
      initialJobSites.current = lots?.map((field: any) => field.jobsite || '');
      form.setValue('singleOrMultiLots', lots);
    });
    setOpenDialog(false);
  };

  const handleSubmitConfirm = async () => {
    const { type, ...dataWithoutType } = transformedValues;
    await mutateAsync({ data: { ...dataWithoutType, generateWorkOrders: true } }).then((data: any) => {
      const lots = data?.data?.singleOrMultiLots;
      initialJobSites.current = lots?.map((field: any) => field.jobsite || '');
      form.setValue('singleOrMultiLots', lots);
    });
    setOpenDialog(false);
  };

  // ── Validation error display ──────────────────────────────────────────────
  const handleError = (error: any) => {
    const messages = parseErrorMessageRecursive(error);
    let requiredMessages: any[] = [];
    let otherMessages: any[] = [];

    Array.isArray(messages) &&
      messages?.forEach((message) => {
        if (message.endsWith('is required')) {
          requiredMessages.push(message?.split(' is required')[0]?.trim());
        } else {
          otherMessages.push(message.trim());
        }
      });

    requiredMessages = Array.from(new Set(requiredMessages));
    otherMessages = Array.from(new Set(otherMessages));

    const finalRequiredMessage = requiredMessages.length > 0 ? `${requiredMessages?.join(', ')} is required` : '';
    const finalOtherMessage = otherMessages?.join(', ');
    const finalMessage = [finalRequiredMessage, finalOtherMessage]?.filter(Boolean)?.join(', ');

    showSnackbar(`Validation Error: ${finalMessage}`, 'error');
  };

  const onTypedSubmit = async (type: string) => {
    form.handleSubmit((values) => handleSubmit({ ...values, type }), handleError)();
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  const disabledBuildingPermit = (index: number) => {
    return form.getValues('singleOrMultiLots')[index]?.projectSyncWithDUX;
  };

  const isProjectTypeRES = true;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <ReactHookForm
        formName="TIME_AND_MATERIAL_FORM"
        onSubmit={handleSubmit}
        formContextProps={form}
        isBusy={isLoading}
      >
        <Card elevation={0}>
          <CardContent style={{ padding: '25px', paddingTop: '30px' }}>
            <Grid container spacing={2}>
              {fields?.map((field: any, index: any) => (
                <Grid key={field.tID} item lg={12} sm={12} xl={12} xs={12}>
                  <Grid container spacing={2}>
                    <Grid item lg={8.4} sm={8.4} xl={8.4} xs={8.4}>
                      <div style={{ display: 'none' }}>
                        <label>{form.getValues(`singleOrMultiLots.${index}.unitNumber`)}</label>
                      </div>
                      <Grid container spacing={1}>
                        <Grid
                          item
                          lg={isProjectTypeRES ? 1.8 : 2}
                          sm={isProjectTypeRES ? 1.8 : 2}
                          xl={isProjectTypeRES ? 1.8 : 2}
                          xs={isProjectTypeRES ? 1.8 : 2}
                        >
                          <FormInput
                            name={`singleOrMultiLots.${index}.jobsite`}
                            onChange={(e: any) => handleJobsiteNameChange(index, e)}
                            label={isTimeAndMaterial ? 'Inspection Title' : 'Jobsite'}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressJobsite(
                                projectID,
                                '',
                                form.getValues(`singleOrMultiLots.${index}.unitID`),
                                '',
                                value
                              )
                            }
                          />
                        </Grid>
                        <Grid
                          item
                          lg={isProjectTypeRES ? 1.7 : 2}
                          sm={isProjectTypeRES ? 1.7 : 2}
                          xl={isProjectTypeRES ? 1.7 : 2}
                          xs={isProjectTypeRES ? 1.7 : 2}
                        >
                          <FormInput
                            name={`singleOrMultiLots.${index}.unitName`}
                            label={'Lot/Unit'}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressUnitName(
                                projectID,
                                '',
                                form.getValues(`singleOrMultiLots.${index}.unitID`),
                                value,
                                ''
                              )
                            }
                            onChange={(e: any) => handleUnitNameChange(index, e)}
                          />
                        </Grid>
                        <Grid item lg={2} sm={2} xl={2} xs={2}>
                          <FormInput
                            onChange={(e: any) => handleJobNumberChange(index, e)}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressJobNumber(
                                projectID,
                                '',
                                form.getValues(`singleOrMultiLots.${index}.unitID`),
                                '',
                                '',
                                value
                              )
                            }
                            name={`singleOrMultiLots.${index}.jobNumber`}
                            label={'Builder Job Number'}
                          />
                        </Grid>
                        <Grid
                          item
                          lg={isProjectTypeRES ? 1.5 : 2}
                          sm={isProjectTypeRES ? 1.5 : 2}
                          xl={isProjectTypeRES ? 1.5 : 2}
                          xs={isProjectTypeRES ? 1.5 : 2}
                        >
                          <FormInput
                            name={`singleOrMultiLots.${index}.address`}
                            label={'Address'}
                            onChange={(e: any) => handleAddressChange(index, e)}
                            isCustomError={true}
                            validationQueryRoute={(value: string) =>
                              projectsApiKeyStore.checkDuplicateLotAddressOfAddress(
                                projectID,
                                value,
                                form.getValues(`singleOrMultiLots.${index}.unitID`)
                              )
                            }
                          />
                        </Grid>
                        <Grid
                          item
                          lg={isProjectTypeRES ? 1.5 : 2}
                          sm={isProjectTypeRES ? 1.5 : 2}
                          xl={isProjectTypeRES ? 1.5 : 2}
                          xs={isProjectTypeRES ? 1.5 : 2}
                        >
                          <FormInput name={`singleOrMultiLots.${index}.squareFeet`} label={'Square Feet'} />
                        </Grid>
                        <Grid item lg={2} sm={2} xl={2} xs={2}>
                          <FormAutoCompleteSelectDynamic
                            name={`singleOrMultiLots.${index}.proctorResult`}
                            label="Proctor Result"
                            optionsValueKey="proctorResultsID"
                            optionsLabelKey="proctorFileName"
                            dataItems={!data ? [] : data?.data}
                          />
                        </Grid>
                        {isProjectTypeRES && (
                          <Grid item lg={1.5} sm={1.5} xl={1.5} xs={1.5}>
                            <FormSelect
                              name={`singleOrMultiLots.${index}.lotType`}
                              label="Lot Type"
                              options={projectTypeValue === 'RES' ? lotTypeOption : lotTypeCMOption}
                              error={true}
                              onChange={(e: any) => handleLotTypeChange(index, e)}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    <Grid item lg={3.6} sm={3.6} xl={3.6} xs={3.6}>
                      <Grid
                        container
                        direction="row"
                        sx={{ justifyContent: 'flex-start', alignItems: 'flex-start' }}
                      >
                        <FormCheckbox label="Create WO" name={`singleOrMultiLots.${index}.createWO`} />
                        <FormCheckbox
                          label="Sync With DUX"
                          name={`singleOrMultiLots.${index}.syncWithDUX`}
                          disabled={!disabledBuildingPermit(index)}
                        />
                        <FormInput
                          label="Building Permit#"
                          name={`singleOrMultiLots.${index}.buildingPermit`}
                          styleField={{ width: '160px' }}
                        />
                        {page &&
                          form.getValues(`singleOrMultiLots.${index}.isUnitCreationCompleted`) &&
                          !!form.getValues(`singleOrMultiLots.${index}.unitID`) && (
                            <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_UNIT]}>
                              <IconButton
                                style={{ padding: '10px 2px 5px 15px' }}
                                aria-label="view"
                                onClick={() =>
                                  navigate(`/units/profile/${form.getValues(`singleOrMultiLots.${index}.unitID`)}`)
                                }
                              >
                                <Tooltip title={<Typography variant="body1">View</Typography>}>
                                  <VisibilityIcon className="btnView" />
                                </Tooltip>
                              </IconButton>
                            </GuardedComponent>
                          )}
                        {!!form.getValues(`singleOrMultiLots.${index}.unitID`) && (
                          <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_UNIT]}>
                            {!form.getValues(`singleOrMultiLots.${index}.disableDelete`) ? (
                              <DeleteButton
                                data={undefined}
                                deleteMutationConfig={() =>
                                  projectsApiKeyStore.deleteLot(form.getValues(`singleOrMultiLots.${index}.unitID`))
                                }
                                onDeleted={() => handleRemove(index)}
                              >
                                <>
                                  <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                    <img src={Icons.Delete} alt="DELETE" />
                                  </Tooltip>
                                </>
                              </DeleteButton>
                            ) : (
                              <DeleteButton
                                data={undefined}
                                deleteMutationConfig={() =>
                                  projectsApiKeyStore.deleteLot(form.getValues(`singleOrMultiLots.${index}.unitID`))
                                }
                                onDeleted={() => handleRemove(index)}
                                isDisabled={true}
                              >
                                <>
                                  <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                    <img src={Icons.DisabledDelete} alt="DELETE" />
                                  </Tooltip>
                                </>
                              </DeleteButton>
                            )}
                          </GuardedComponent>
                        )}
                      </Grid>
                    </Grid>
                    {field?.showMysiStatusdrop && (
                      <Grid item lg={1} sm={1} xl={1} xs={1}>
                        <FormSelectDynamic
                          name={`singleOrMultiLots.${index}.reqStatusID`}
                          label="Request Status"
                          getQueryKeyConfig={() => projectsApiKeyStore.getProjectRequestStatus()}
                          optionsValueKey="reqStatusID"
                          optionsLabelKeys={['reqStatus']}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </CardContent>

          <Grid item lg={1} sm={1} xl={1} xs={1}>
            {isTimeAndMaterial ? (
              <CreateTimeAndMaterialButton
                projectID={projectID}
                syncWithDux={syncWithDux}
                onUnitCreated={handleLoadCreated}
              />
            ) : (
              <CreateBuildingFloorUnitButton
                projectID={projectID}
                projectTypeValue={projectTypeValue ?? ''}
                onUnitCreated={handleLoadCreated}
                floor={{ floorID: undefined }}
                existingData={form.getValues('singleOrMultiLots')}
                syncWithDux={syncWithDux}
              />
            )}
          </Grid>
        </Card>

        <Grid
          item
          lg={12}
          sm={12}
          xl={12}
          xs={12}
          style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}
        >
          <>
            <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
              <SampleFileForDownload />
            </GuardedComponent>
            <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
              <Button
                type="button"
                onClick={() => setIsBulkAddressDialogOpen(true)}
                className="btn btn-primary btnIcon"
                variant="text"
                sx={{ p: 0, m: 0, mr: 1 }}
              >
                Bulk Address Upload
              </Button>
            </GuardedComponent>
            <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
              <Button
                type="button"
                onClick={() => setIsBulkLotDialogOpen(true)}
                className="btn btn-primary btnIcon"
                variant="text"
                sx={{ p: 0, m: 0, mr: 1 }}
              >
                Bulk Lot Creation
              </Button>
            </GuardedComponent>
          </>
          <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
            <Button
              type="button"
              disabled={!form.getValues(`singleOrMultiLots`) || form.getValues(`singleOrMultiLots`)?.length === 0}
              className="btn btn-primary btnIcon"
              variant="text"
              style={{
                backgroundColor:
                  !form.getValues(`singleOrMultiLots`) || form.getValues(`singleOrMultiLots`)?.length === 0
                    ? 'gray'
                    : '#e67e22',
              }}
              sx={{ p: 0, m: 0, mr: 1 }}
              onClick={() => onTypedSubmit('without-workorder')}
            >
              Create Lots/Units without workorders
            </Button>
          </GuardedComponent>
          <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
            <Button
              type="button"
              disabled={!form.getValues(`singleOrMultiLots`) || form.getValues(`singleOrMultiLots`)?.length === 0}
              className="btn btn-primary btnIcon"
              variant="text"
              style={{
                backgroundColor:
                  !form.getValues(`singleOrMultiLots`) || form.getValues(`singleOrMultiLots`)?.length === 0
                    ? 'gray'
                    : '#239b56',
              }}
              sx={{ p: 0, m: 0, mr: 1 }}
              onClick={() => onTypedSubmit('with-workorder')}
            >
              Create Lots/Units with workorder
            </Button>
          </GuardedComponent>
        </Grid>
      </ReactHookForm>

      {/* ── Work Order Confirmation Dialog ────────────────────────────────── */}
      {openDialog && (
        <Dialog open={openDialog} className="modalpopup" title={'CONFIRM STATUS'}>
          <div>
            <div style={{ padding: '25px 50px', textAlign: 'center' }}>
              <div>Are you sure you want to generate work orders for all the newly added Lot/Units?</div>
            </div>
          </div>
          <DialogActionsBar>
            {isReadOnlyPermission && (
              <Button
                style={{ maxWidth: '140px' }}
                size="small"
                type="button"
                variant="outlined"
                onClick={() => setOpenDialog(false)}
              >
                Cancel
              </Button>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }}>
              <button
                className="btn btn-sm btn-primary btn-lightGrey"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer',
                  padding: 2,
                  marginRight: 6,
                }}
                disabled={isReadOnlyPermission}
                onClick={closeDialog}
                autoFocus
              >
                No
              </button>
              <button
                className="btn btn-sm btn-primary btn-delete"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer',
                  padding: 2,
                }}
                type="button"
                onClick={handleSubmitConfirm}
                disabled={isLoading || isReadOnlyPermission}
              >
                Yes
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* ── ✅ Lot Type Pricing Confirmation Dialog ────────────────────────── */}
      {/* Only shown when isUnitCreationCompleted = true AND lot type genuinely changed */}
      {lotTypeChangeDialog.open && (
        <Dialog open={lotTypeChangeDialog.open} className="modalpopup">
          <div style={{ padding: '25px 50px', textAlign: 'center' }}>
            <Typography variant="body1">
              Do you want to update the pricing for this lot&apos;s work orders based on the selected lot type
              change?
            </Typography>
          </div>
          <DialogActionsBar>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', padding: '8px' }}>
              <button
                className="btn btn-sm btn-primary"
                style={{ backgroundColor: '#0000b5', color: '#fff', cursor: 'pointer', padding: '6px 16px' }}
                onClick={handleLotTypeConfirmYes}
              >
                Yes
              </button>
              <button
                className="btn btn-sm btn-primary btn-lightGrey"
                style={{ backgroundColor: '#d7dbdd', color: 'black', cursor: 'pointer', padding: '6px 16px' }}
                onClick={handleLotTypeConfirmNo}
              >
                No
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* ── Bulk Lot Upload Dialog ────────────────────────────────────────── */}
      {isBulkLotDialogOpen && (
        <GenericFileUploadDialog
          title="Upload File For Bulk Lot Creation"
          defaultData={{}}
          fileRequestArgs={{}}
          isLoading={isLoadingFile}
          onSubmit={(formData) => handleSubmitBulkLotCreationFileSave(formData)}
          onClose={() => setIsBulkLotDialogOpen(false)}
          acceptedFileTypes={{
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          }}
        />
      )}

      {/* ── Bulk Address Upload Dialog ────────────────────────────────────── */}
      {isBulkAddressDialogOpen && (
        <GenericFileUploadDialog
          title="Upload File For Bulk Address Creation"
          defaultData={{}}
          fileRequestArgs={{}}
          isLoading={isLoadingFileForAddressUpload}
          onSubmit={(formData) => handleSubmitBulkAddressCreationFileSave(formData)}
          onClose={() => setIsBulkAddressDialogOpen(false)}
          acceptedFileTypes={{
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          }}
        />
      )}
    </div>
  );
};